import React, { useEffect, useState } from "react";
import styled from "styled-components";
import logo from "../assets/logo.png";
import { FaBars } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { links } from "../utils/constants";
import CartButtons from "./CartButtons";
import { useProductsContext } from "../context/products_context";

const Nav = () => {
  const { openSidebar } = useProductsContext();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // navbar is transparent only on home page AND when at top of page
  const transparent = isHome && !scrolled;

  return (
    <NavContainer className={transparent ? "transparent" : "solid"}>
      <div className="nav-center">
        <div className="nav-header">
          <Link to="/" className="logo-link">
            <img src={logo} alt="Bushman Bar Service" className="logo" />
            <span className="script tagline">Pardubice</span>
          </Link>
          <button
            type="button"
            className="nav-toggle"
            onClick={openSidebar}
            aria-label="Otevřít menu"
          >
            <FaBars />
          </button>
        </div>
        <ul className="nav-links">
          {links.map((link) => {
            const { id, text, url } = link;
            const active = pathname === url;
            return (
              <li key={id}>
                <Link to={url} className={active ? "active" : ""}>
                  <span className="roll" aria-hidden="false">
                    <span className="roll-inner">
                      <span className="roll-copy">{text}</span>
                      <span className="roll-copy alt" aria-hidden="true">
                        {text}
                      </span>
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        <CartButtons />
      </div>
    </NavContainer>
  );
};

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  height: 5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  background-image: linear-gradient(
    to bottom,
    rgba(15, 10, 6, 0.45) 0%,
    rgba(15, 10, 6, 0) 100%
  );
  border-bottom: 1px solid transparent;
  box-shadow: none;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  transition: background-color 400ms ease, background-image 400ms ease,
    box-shadow 400ms ease, height 400ms cubic-bezier(0.22, 1, 0.36, 1),
    border-color 400ms ease, backdrop-filter 400ms ease;

  &.solid {
    height: 4.25rem;
    background-color: rgba(22, 15, 11, 0.82);
    background-image: none;
    backdrop-filter: blur(14px) saturate(140%);
    -webkit-backdrop-filter: blur(14px) saturate(140%);
    border-bottom: 1px solid rgba(184, 115, 51, 0.28);
    box-shadow: 0 8px 24px rgba(15, 10, 6, 0.25);
  }

  .logo-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .logo {
    max-height: 60px;
    max-width: 60px;
    margin-right: 15px;
    filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.3));
    transition: transform 400ms cubic-bezier(0.22, 1, 0.36, 1);
    transform-origin: left center;
  }
  &.solid .logo {
    transform: scale(0.82);
  }
  .tagline {
    display: none;
    font-size: 1.3rem;
    color: var(--clr-accent-light);
    margin-bottom: -0.25rem;
  }
  .nav-center {
    width: 90vw;
    margin: 0 auto;
    max-width: var(--max-width);
  }
  .nav-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    img {
      width: 175px;
    }
  }
  .nav-toggle {
    background: transparent;
    border: transparent;
    color: var(--clr-cream);
    cursor: pointer;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius);
    transition: var(--transition);
    svg {
      font-size: 1.75rem;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
    }
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
    &:active {
      transform: scale(0.95);
    }
  }
  .nav-links {
    display: none;
    list-style: none;
  }
  .cart-btn-wrapper {
    display: none;
  }

  @media (min-width: 992px) {
    .logo {
      max-height: 90px;
      max-width: 90px;
    }
    .tagline {
      display: inline-block;
      text-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
    }
    .nav-toggle {
      display: none;
    }
    .nav-center {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: 2rem;
    }
    .nav-links {
      display: flex;
      justify-content: center;
      gap: 0.5rem;

      a {
        position: relative;
        display: inline-block;
        color: var(--clr-cream);
        font-family: var(--ff-body);
        font-size: 0.8rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.14em;
        padding: 0.55rem 1rem;
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
      }

      /* rolling label — second copy slides up on hover */
      .roll {
        display: block;
        overflow: hidden;
        height: 1.25em;
      }
      .roll-inner {
        display: flex;
        flex-direction: column;
        transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      .roll-copy {
        display: block;
        height: 1.25em;
        line-height: 1.25em;
      }
      .roll-copy.alt {
        color: var(--clr-accent-light);
      }
      a:hover .roll-inner {
        transform: translateY(-1.25em);
      }

      /* active page — copper dot under the label */
      a::after {
        content: "";
        position: absolute;
        left: 50%;
        bottom: -0.1rem;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: var(--clr-accent);
        transform: translateX(-50%) scale(0);
        transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      a.active::after {
        transform: translateX(-50%) scale(1);
      }
    }
    .cart-btn-wrapper {
      display: flex;
    }
  }
`;

export default Nav;
